// Per-user column visibility for the TableView. The "Titel" column is always
// visible; everything else can be toggled and persisted in localStorage.
//
// Zusätzlich wohnen hier ALLE Layout-Maße der Tabelle (Spurbreiten, Lücke,
// Innenabstand, Mindestbreiten). Siehe „Ausrichtung" weiter unten.

import type { LayoutStyle } from "./layout";

export type TableColumnId =
  | "type"
  | "status"
  | "assignees"
  | "labels"
  | "start"
  | "end"
  | "priority"
  | "ticket";

export const COLUMN_LABEL: Record<TableColumnId, string> = {
  type: "Typ",
  status: "Status",
  assignees: "Zuständig",
  labels: "Labels",
  start: "Start",
  end: "Ende",
  priority: "Prio",
  ticket: "Ticket",
};

export const COLUMN_IDS: TableColumnId[] = [
  "type",
  "status",
  "assignees",
  "labels",
  "start",
  "end",
  "priority",
  "ticket",
];

const STORAGE_KEY = "juuplan:hidden-cols";

/**
 * Standard-Versteckt-Satz. Alle zwölf Spuren zusammen passen bei 1440 px
 * Fensterbreite nicht in die ~1136 px Inhaltsfläche (Rechnung in
 * `gridMinWidthPx`); der Titel wurde deshalb auf 0 px gequetscht. Ticket, Typ
 * und Labels starten daher ausgeblendet und sind über das „Spalten"-Menü
 * jederzeit wieder einschaltbar.
 */
export const DEFAULT_HIDDEN_COLUMNS: readonly TableColumnId[] = [
  "ticket",
  "type",
  "labels",
];

export function defaultHidden(): Set<TableColumnId> {
  return new Set(DEFAULT_HIDDEN_COLUMNS);
}

function isColumnId(v: unknown): v is TableColumnId {
  return (
    v === "type" ||
    v === "status" ||
    v === "assignees" ||
    v === "labels" ||
    v === "start" ||
    v === "end" ||
    v === "priority" ||
    v === "ticket"
  );
}

/**
 * Gespeicherte Wahl vs. Default — die Unterscheidung hängt allein an der
 * ANWESENHEIT des Schlüssels, nicht an seinem Inhalt: `saveHidden` schreibt
 * auch die leere Auswahl als `"[]"`. Wer das Spalten-Menü je benutzt hat, hat
 * also einen Schlüssel, und dessen Inhalt gewinnt — auch wenn er genau das
 * Gegenteil des neuen Defaults sagt („alles sichtbar"). Nur wenn gar kein
 * Schlüssel liegt (oder er unbrauchbar ist), greift `defaultHidden()`. So
 * kippt ein Default-Wechsel keine gespeicherte Wahl still um.
 */
export function loadHidden(): Set<TableColumnId> {
  if (typeof localStorage === "undefined") return defaultHidden();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null || raw === "") return defaultHidden();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultHidden();
    const out = new Set<TableColumnId>();
    for (const v of parsed) if (isColumnId(v)) out.add(v);
    return out;
  } catch {
    return defaultHidden();
  }
}

export function saveHidden(s: Set<TableColumnId>): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(s)));
}

// ---------------------------------------------------------------------------
// Ausrichtung
//
// Kopf (TableView) und beide Zeilen-Varianten (TaskRow, TaskRowKompakt) lesen
// Spurbreiten UND Abstände ausschließlich hier. Die frühere Fassung hat die
// Ausrichtung nur per Kommentar zugesichert („all three call this one helper
// so the alignment can never drift") — diese Zusicherung war falsch: der
// Helfer garantierte NUR die Spurbreiten. Der Kopf lief mit `gap-2 px-3`, die
// Komfort-Zeile mit `gap-3 px-4`; das ergab 11x4 px + 4 px = 48 px Versatz.
// Lücke und Innenabstand sind deshalb jetzt Konstanten, die alle drei Stellen
// benutzen — ein Versatz ist ohne Änderung DIESER Werte nicht mehr möglich.
// ---------------------------------------------------------------------------

export const GRID_GAP_CLASS: Record<LayoutStyle, string> = {
  komfort: "gap-3",
  kompakt: "gap-2",
};

export const GRID_GAP_PX: Record<LayoutStyle, number> = {
  komfort: 12,
  kompakt: 8,
};

export const GRID_PADDING_X_CLASS: Record<LayoutStyle, string> = {
  komfort: "px-4",
  kompakt: "px-3",
};

export const GRID_PADDING_X_PX: Record<LayoutStyle, number> = {
  komfort: 16,
  kompakt: 12,
};

/** Lücke + Innenabstand als eine Klassen-Zeichenkette für Kopf und Zeile. */
export function gridSpacingClass(density: LayoutStyle = "komfort"): string {
  return `${GRID_GAP_CLASS[density]} ${GRID_PADDING_X_CLASS[density]}`;
}

/** Mindestbreite der Titelspur — sie darf nie auf 0 kollabieren. */
export const TITLE_MIN_PX: Record<LayoutStyle, number> = {
  komfort: 240,
  kompakt: 200,
};

/**
 * Inhaltsfläche bei 1440 px Fensterbreite (1440 minus 240 px Sidebar minus
 * Ränder). Referenzwert für die Tests: der Standardfall muss hier ohne
 * waagerechten Scroll passen.
 */
export const CONTENT_WIDTH_1440_PX = 1136;

/**
 * Build the CSS grid-template-columns string based on which columns are
 * hidden. Width values differ between "komfort" (airier default rows) and
 * "kompakt" (Monday-style dense rows); the column order and count are the
 * same for both, only the pixel widths change.
 *
 * Die Titelspur ist `minmax(<min>px,1fr)` statt `1fr` — mit nacktem `1fr`
 * kollabierte sie auf 0 px, sobald die festen Spuren plus Lücken breiter
 * waren als die Inhaltsfläche (genau das war der Fehler bei 1440 px).
 *
 * ACHTUNG: kein Leerzeichen in `minmax(...)`. Die Spuren werden an
 * Leerzeichen getrennt gezählt (buildGridTemplate.test.ts) und geparst
 * (`trackMinPx`); ein Leerzeichen im minmax würde eine Spur doppelt zählen.
 */
export function buildGridTemplate(
  hidden: Set<TableColumnId>,
  density: LayoutStyle = "komfort"
): string {
  const title = `minmax(${TITLE_MIN_PX[density]}px,1fr)`;
  const parts: string[] =
    density === "kompakt" ? ["16px", "24px", title] : ["36px", "32px", title];
  if (!hidden.has("ticket")) parts.push("70px");
  if (!hidden.has("type")) parts.push("120px");
  if (!hidden.has("status")) parts.push(density === "kompakt" ? "120px" : "130px");
  if (!hidden.has("assignees")) parts.push(density === "kompakt" ? "120px" : "110px");
  if (!hidden.has("labels")) parts.push("160px");
  if (!hidden.has("start")) parts.push("120px");
  if (!hidden.has("end")) parts.push("120px");
  if (!hidden.has("priority")) parts.push(density === "kompakt" ? "100px" : "110px");
  parts.push("68px");
  return parts.join(" ");
}

/** Mindestbreite EINER Spur in px — `70px` -> 70, `minmax(240px,1fr)` -> 240. */
function trackMinPx(track: string): number {
  const mm = /^minmax\((\d+)px,/.exec(track);
  if (mm !== null) return Number(mm[1]);
  const px = /^(\d+)px$/.exec(track);
  return px === null ? 0 : Number(px[1]);
}

/** Die Spuren als Liste — gleiche Quelle wie `buildGridTemplate`. */
export function trackList(
  hidden: Set<TableColumnId>,
  density: LayoutStyle = "komfort"
): string[] {
  return buildGridTemplate(hidden, density).split(" ");
}

/**
 * Mindestbreite der ganzen Zeile inklusive Innenabstand — als `min-width` auf
 * Kopf und Zeilen gesetzt. Ohne sie quetscht der Grid-Algorithmus die Spuren
 * in den Container; mit ihr läuft die Zeile über und der Container (mit
 * `overflow-x-auto`) scrollt waagerecht, statt Spalten zu zerdrücken.
 */
export function gridMinWidthPx(
  hidden: Set<TableColumnId>,
  density: LayoutStyle = "komfort"
): number {
  const tracks = trackList(hidden, density);
  const sum = tracks.reduce((acc, t) => acc + trackMinPx(t), 0);
  return (
    sum +
    (tracks.length - 1) * GRID_GAP_PX[density] +
    2 * GRID_PADDING_X_PX[density]
  );
}

export function gridMinWidth(
  hidden: Set<TableColumnId>,
  density: LayoutStyle = "komfort"
): string {
  return `${gridMinWidthPx(hidden, density)}px`;
}

// ---------------------------------------------------------------------------
// Eingefrorene Titelspalte
//
// `position: sticky` auf einer Grid-Zelle ist wirkungslos: der Versatz einer
// sticky Box wird auf ihren umgebenden Block begrenzt, und der umgebende Block
// eines Grid-Items IST seine Grid-Area — die Zelle kann sich darin nicht
// bewegen. Deshalb ist die Zeile ein Flex-Container mit ZWEI Grid-Kindern:
// links der eingefrorene Block (Griff, Erledigt-Kreis, Titel), rechts die
// mitscrollenden Spalten. Flex-Items haben den ganzen Flex-Container als
// umgebenden Block, dort greift sticky wirklich.
// ---------------------------------------------------------------------------

/** Anzahl Spuren im eingefrorenen Block: Griff, Erledigt-Kreis, Titel. */
export const FROZEN_TRACK_COUNT = 3;

export function splitGridTemplate(
  hidden: Set<TableColumnId>,
  density: LayoutStyle = "komfort"
): { frozen: string; scrolling: string } {
  const tracks = trackList(hidden, density);
  return {
    frozen: tracks.slice(0, FROZEN_TRACK_COUNT).join(" "),
    scrolling: tracks.slice(FROZEN_TRACK_COUNT).join(" "),
  };
}

/**
 * Mindestbreite des eingefrorenen Blocks. Er trägt den linken Innenabstand
 * mit (negativer Rand + gleicher Innenabstand), damit sein Hintergrund beim
 * Scrollen bis an die linke Kante deckt und keine Zelle darunter durchblitzt.
 */
export function frozenMinWidthPx(density: LayoutStyle = "komfort"): number {
  const tracks = trackList(new Set(), density).slice(0, FROZEN_TRACK_COUNT);
  const sum = tracks.reduce((acc, t) => acc + trackMinPx(t), 0);
  return (
    sum +
    (FROZEN_TRACK_COUNT - 1) * GRID_GAP_PX[density] +
    GRID_PADDING_X_PX[density]
  );
}

/**
 * Hintergrund der Zeile. Deckend, nicht durchscheinend: der eingefrorene
 * Block erbt diesen Wert (`background-color: inherit`) und läge sonst als
 * zweite 8-%-Lage über der Zeile — beim Scrollen blitzte ein falscher Ton auf.
 * Wird als CSS-Variable gesetzt, damit die `hover:`-Klasse der Zeile weiter
 * gewinnt (ein Inline-`background` würde sie aushebeln).
 */
export const ROW_BG_VAR = "--juuplan-row-bg";
export const ROW_BG_BASE = "var(--color-surface-container-lowest)";
export const ROW_BG_SELECTED =
  "color-mix(in srgb, var(--color-primary) 8%, var(--color-surface-container-lowest))";
